import React, { useEffect, useContext, useState } from "react";
import styled from "styled-components";
import { Typography, COLORS, Icon } from "@virkesborsen/ui-component-library";
import { MetricCard } from "./components/MetricCard";
import { useDFR } from "../context/DFRContext";
import { PropsData } from "../context";
import { Spinner } from "react-bootstrap";

const WeatherCard = styled.div`
  background-color: ${COLORS["cloud-50"]};
  border: 1px solid #e2e1e0;
  border-radius: 16px;
  padding: 24px;
`;
const Designation = styled.span`
  text-transform: capitalize;
`;

const translateDegreesToDirection = (value) => {
  const compassDirections = [
    { direction: "Nord", abbreviation: "N", min: 337.5, max: 22.5 },
    { direction: "Nordost", abbreviation: "NO", min: 22.5, max: 67.5 },
    { direction: "Öst", abbreviation: "Ö", min: 67.5, max: 112.5 },
    { direction: "Sydost", abbreviation: "SO", min: 112.5, max: 157.5 },
    { direction: "Syd", abbreviation: "S", min: 157.5, max: 202.5 },
    { direction: "Sydväst", abbreviation: "SV", min: 202.5, max: 247.5 },
    { direction: "Väst", abbreviation: "V", min: 247.5, max: 292.5 },
    { direction: "Nordväst", abbreviation: "NV", min: 292.5, max: 337.5 },
  ];
  const matchingDirection = compassDirections.find(
    (direction) => value > direction.min && value < direction.max
  );

  return matchingDirection?.direction;
};

export default function WeatherDetails() {
  const propsContext = useContext(PropsData);
  const DFRContext = useDFR();
  const { weatherData } = DFRContext;

  const dfrProperties = propsContext.props.filter(
    (properties) => properties.dfr
  );

  const activeProperties = Object.entries(weatherData).filter(([lmID]) =>
    dfrProperties.some((dfr) => dfr.ref === lmID && dfr.active)
  );

  const formatDesignationName = (name) => (
    <Designation>{name.toLowerCase() || ""}</Designation>
  );

  const hasDataFailed = (dataObject) => {
    if (!dataObject || typeof dataObject !== "object") return true;
    return Object.values(dataObject).every((value) => value === null);
  };

  const WeatherCardContent = ({
    title,
    description,
    isLoading,
    hasError,
    children,
  }) => (
    <WeatherCard>
      <div className="d-flex flex-column gap-3">
        <div>
          <Typography
            variant="subheaderSmallMedium"
            color="charcoal-500"
            className="mb-1"
          >
            {title}
          </Typography>
          <Typography variant="bodyMediumBaseRegular" color="pebble-500">
            {description}
          </Typography>
        </div>

        <div className="d-flex gap-sm-1 gap-md-3 gap-lg-6 justify-content-around">
          {isLoading && (
            <div className="pt-3">
              <Spinner animation="border" variant="primary" />
            </div>
          )}
          {hasError && !isLoading && (
            <MetricCard
              key={"error"}
              label={"Gick ej att hämta data"}
              value={" "}
              icon={"CrossSimple"}
              bgColor={"red-scarlet-50"}
              iconColor={"red-scarlet-500"}
              rotation={0}
              suffix={""}
            />
          )}
          {!isLoading && !hasError && children}
        </div>
      </div>
    </WeatherCard>
  );

  if (dfrProperties.length > 0 && activeProperties.length === 0) {
    return (
      <>
        {dfrProperties
          .filter((property) => property.active)
          .map((property) => (
            <div key={property.ref} className="d-flex flex-column gap-4 mb-4">
              <div className="d-flex flex-column gap-2">
                <div className="d-flex flex-row">
                  <Typography
                    variant="displayAndHeadline2XtraSmallMedium"
                    color="charcoal-500"
                  >
                    Väderdata över din skogsfastighet{" "}
                    {formatDesignationName(property.designation)}
                  </Typography>
                </div>
                <Typography
                  variant="introLeadMediumRegular"
                  color="charcoal-500"
                >
                  Här kan du se den senaste väderstatistiken över din fastighet
                </Typography>
              </div>

              <div className="row g-3 g-lg-4">
                <div className="col-12 col-xl-6">
                  <WeatherCardContent
                    title="Temperatur"
                    description="Senaste temperaturinformation över din fastighet"
                    isLoading={true}
                    hasError={false}
                  />
                </div>
                <div className="col-12 col-xl-6">
                  <WeatherCardContent
                    title="Vind"
                    description="Senaste vindinformation över din fastighet"
                    isLoading={true}
                    hasError={false}
                  />
                </div>
                <div className="col-12 col-xl-6">
                  <WeatherCardContent
                    title="Nederbörd"
                    description="Senaste nederbördinformation över din fastighet"
                    isLoading={true}
                    hasError={false}
                  />
                </div>
                <div className="col-12 col-xl-6">
                  <WeatherCardContent
                    title="Övrigt"
                    description="Senaste övrigt information över din fastighet"
                    isLoading={true}
                    hasError={false}
                  />
                </div>
              </div>
            </div>
          ))}
      </>
    );
  }

  return (
    <>
      {activeProperties.map(([_, { designation, today, historic }]) => {
        const failedFetchingToday = hasDataFailed(today);
        const failedFetchingHistoric = hasDataFailed(historic);

        return (
          <div key={designation} className="d-flex flex-column gap-4 mb-4">
            <div className="d-flex flex-column gap-2">
              <div className="d-flex flex-row">
                <Typography
                  variant="displayAndHeadline2XtraSmallMedium"
                  color="charcoal-500"
                >
                  Väderdata över din skogsfastighet{" "}
                  {formatDesignationName(designation)}
                </Typography>
              </div>
              <Typography variant="introLeadMediumRegular" color="charcoal-500">
                Här kan du se den senaste väderstatistiken över din fastighet
              </Typography>
            </div>
            <Typography variant="subheaderMediumMedium" color="charcoal-500">
              Senaste 24h
            </Typography>

            <div className="row g-3 g-lg-4">
              <div className="col-12 col-xl-6">
                <WeatherCardContent
                  title="Temperatur"
                  description="Senaste temperaturinformation över din fastighet"
                  isLoading={false}
                  hasError={failedFetchingToday}
                >
                  <>
                    <MetricCard
                      key={"avg"}
                      label={"Genomsnitt"}
                      value={today?.Lufttemperatur?.avg ?? "-"}
                      icon={"ThermometerSimple"}
                      bgColor={"red-scarlet-50"}
                      iconColor={"red-scarlet-500"}
                      rotation={0}
                      suffix={"°"}
                    />
                    <MetricCard
                      key={"high"}
                      label={"Högst"}
                      value={today?.Lufttemperatur?.max ?? "-"}
                      icon={"ArrowRightSimple"}
                      bgColor={"red-scarlet-50"}
                      iconColor={"red-scarlet-500"}
                      rotation={270}
                      suffix={"°"}
                    />
                    <MetricCard
                      key={"low"}
                      label={"Lägst"}
                      value={today?.Lufttemperatur?.min ?? "-"}
                      icon={"ArrowRightSimple"}
                      bgColor={"red-scarlet-50"}
                      iconColor={"red-scarlet-500"}
                      rotation={90}
                      suffix={"°"}
                    />
                  </>
                </WeatherCardContent>
              </div>
              <div className="col-12 col-xl-6">
                <WeatherCardContent
                  title="Vind"
                  description="Senaste vindinformation över din fastighet"
                  isLoading={false}
                  hasError={failedFetchingToday}
                >
                  <>
                    <MetricCard
                      key={"byvind"}
                      label={"Byvind"}
                      value={today?.Byvind?.avg ?? "-"}
                      icon={"WindSimple"}
                      bgColor={"charcoal-50"}
                      iconColor={"charcoal-500"}
                      rotation={0}
                      suffix={" m/s"}
                    />
                    <MetricCard
                      key={"wind_avg"}
                      label={"Vindstyrka"}
                      value={today?.Vindhastighet?.avg ?? "-"}
                      icon={"WindSimple"}
                      bgColor={"charcoal-50"}
                      iconColor={"charcoal-500"}
                      rotation={0}
                      suffix={" m/s"}
                    />
                    <MetricCard
                      key={"wind_dir"}
                      label={"Vindriktning"}
                      value={
                        translateDegreesToDirection(today?.Vindriktning?.avg) ??
                        "-"
                      }
                      icon={"CompassSimple"}
                      bgColor={"charcoal-50"}
                      iconColor={"charcoal-500"}
                      rotation={0}
                      suffix={""}
                    />
                  </>
                </WeatherCardContent>
              </div>

              <div className="col-12 col-xl-6">
                <WeatherCardContent
                  title="Nederbörd"
                  description="Senaste nederbördinformation över din fastighet"
                  isLoading={false}
                  hasError={failedFetchingToday}
                >
                  <>
                    <MetricCard
                      key={"per_amount"}
                      label={"Mängd"}
                      value={today?.Nederbördsmängd?.sum ?? "-"}
                      icon={"WaterDropSimple"}
                      bgColor={"purple-plum-50"}
                      iconColor={"purple-plum-500"}
                      rotation={0}
                      suffix={" mm"}
                    />
                    <MetricCard
                      key={"per_type"}
                      label={"Typ"}
                      value={today?.Nederbörd?.most ?? "-"}
                      icon={"RainyCloudSimple"}
                      bgColor={"purple-plum-50"}
                      iconColor={"purple-plum-500"}
                      rotation={0}
                      suffix={""}
                    />
                    <MetricCard
                      key={"sno_depth"}
                      label={"Snö djup"}
                      value={today?.Snödjup?.max ?? "-"}
                      icon={"SnowflakeSimple"}
                      bgColor={"purple-plum-50"}
                      iconColor={"purple-plum-500"}
                      rotation={0}
                      suffix={" m"}
                    />
                  </>
                </WeatherCardContent>
              </div>

              <div className="col-12 col-xl-6">
                <WeatherCardContent
                  title="Övrigt"
                  description="Senaste övrigt information över din fastighet"
                  isLoading={false}
                  hasError={failedFetchingToday}
                >
                  <>
                    <MetricCard
                      key={"sun_hours"}
                      label={"Soltimmar"}
                      value={today?.Solskenstid?.sum / 60 / 60 ?? "-"}
                      icon={"ClockSimple"}
                      bgColor={"green-spruce-50"}
                      iconColor={"green-spruce-500"}
                      rotation={0}
                      suffix={" h"}
                    />
                    <MetricCard
                      key={"air_moisture"}
                      label={"Relativ luftfuktighet"}
                      value={today?.["Relativ Luftfuktighet"]?.avg ?? "-"}
                      icon={"DropHalfBottomSimple"}
                      bgColor={"green-spruce-50"}
                      iconColor={"green-spruce-500"}
                      rotation={0}
                      suffix={"%"}
                    />
                  </>
                </WeatherCardContent>
              </div>
            </div>

            <Typography variant="subheaderMediumMedium" color="charcoal-500">
              Senaste 30 dagarna
            </Typography>

            <div className="row g-3 g-lg-4">
              <div className="col-12 col-xl-6">
                <WeatherCardContent
                  title="Temperatur"
                  description="Senaste temperaturinformation över din fastighet"
                  isLoading={false}
                  hasError={failedFetchingHistoric}
                >
                  <>
                    <MetricCard
                      key={"avg"}
                      label={"Genomsnitt"}
                      value={historic?.Lufttemperatur?.avg ?? "-"}
                      icon={"ThermometerSimple"}
                      bgColor={"red-scarlet-50"}
                      iconColor={"red-scarlet-500"}
                      rotation={0}
                      suffix={"°"}
                    />
                    <MetricCard
                      key={"high"}
                      label={"Högst"}
                      value={historic?.Lufttemperatur?.max ?? "-"}
                      icon={"ArrowRightSimple"}
                      bgColor={"red-scarlet-50"}
                      iconColor={"red-scarlet-500"}
                      rotation={270}
                      suffix={"°"}
                    />
                    <MetricCard
                      key={"low"}
                      label={"Lägst"}
                      value={historic?.Lufttemperatur?.min ?? "-"}
                      icon={"ArrowRightSimple"}
                      bgColor={"red-scarlet-50"}
                      iconColor={"red-scarlet-500"}
                      rotation={90}
                      suffix={"°"}
                    />
                  </>
                </WeatherCardContent>
              </div>
              <div className="col-12 col-xl-6">
                <WeatherCardContent
                  title="Vind"
                  description="Senaste vindinformation över din fastighet"
                  isLoading={false}
                  hasError={failedFetchingHistoric}
                >
                  <>
                    <MetricCard
                      key={"byvind"}
                      label={"Byvind"}
                      value={historic?.Byvind?.avg ?? "-"}
                      icon={"WindSimple"}
                      bgColor={"charcoal-50"}
                      iconColor={"charcoal-500"}
                      rotation={0}
                      suffix={" m/s"}
                    />
                    <MetricCard
                      key={"wind_avg"}
                      label={"Vindstyrka"}
                      value={historic?.Vindhastighet?.avg ?? "-"}
                      icon={"WindSimple"}
                      bgColor={"charcoal-50"}
                      iconColor={"charcoal-500"}
                      rotation={0}
                      suffix={" m/s"}
                    />
                    <MetricCard
                      key={"wind_dir"}
                      label={"Vindriktning"}
                      value={
                        translateDegreesToDirection(
                          historic?.Vindriktning?.avg
                        ) ?? "-"
                      }
                      icon={"CompassSimple"}
                      bgColor={"charcoal-50"}
                      iconColor={"charcoal-500"}
                      rotation={0}
                      suffix={""}
                    />
                  </>
                </WeatherCardContent>
              </div>

              <div className="col-12 col-xl-6">
                <WeatherCardContent
                  title="Nederbörd"
                  description="Senaste nederbördinformation över din fastighet"
                  isLoading={false}
                  hasError={failedFetchingHistoric}
                >
                  <>
                    <MetricCard
                      key={"per_amount"}
                      label={"Mängd"}
                      value={historic?.Nederbördsmängd?.sum ?? "-"}
                      icon={"WaterDropSimple"}
                      bgColor={"purple-plum-50"}
                      iconColor={"purple-plum-500"}
                      rotation={0}
                      suffix={" mm"}
                    />
                    <MetricCard
                      key={"per_type"}
                      label={"Typ"}
                      value={historic?.Nederbörd?.most ?? "-"}
                      icon={"RainyCloudSimple"}
                      bgColor={"purple-plum-50"}
                      iconColor={"purple-plum-500"}
                      rotation={0}
                      suffix={""}
                    />
                    <MetricCard
                      key={"sno_depth"}
                      label={"Snö djup"}
                      value={historic?.Snödjup?.max ?? "-"}
                      icon={"SnowflakeSimple"}
                      bgColor={"purple-plum-50"}
                      iconColor={"purple-plum-500"}
                      rotation={0}
                      suffix={" m"}
                    />
                  </>
                </WeatherCardContent>
              </div>
              <div className="col-12 col-xl-6">
                <WeatherCardContent
                  title="Övrigt"
                  description="Senaste övrigt information över din fastighet"
                  isLoading={false}
                  hasError={failedFetchingHistoric}
                >
                  <>
                    <MetricCard
                      key={"sun_hours"}
                      label={"Soltimmar"}
                      value={historic?.Solskenstid?.sum / 60 / 60 ?? "-"}
                      icon={"ClockSimple"}
                      bgColor={"green-spruce-50"}
                      iconColor={"green-spruce-500"}
                      rotation={0}
                      suffix={" h"}
                    />
                    <MetricCard
                      key={"air_moisture"}
                      label={"Relativ luftfuktighet"}
                      value={historic?.["Relativ Luftfuktighet"]?.avg ?? "-"}
                      icon={"DropHalfBottomSimple"}
                      bgColor={"green-spruce-50"}
                      iconColor={"green-spruce-500"}
                      rotation={0}
                      suffix={"%"}
                    />
                  </>
                </WeatherCardContent>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}
